import React, { Fragment, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel
} from '@mui/material';
import { AppActions } from 'src/store/AppReducer';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { RootStateType } from 'src/store/RootReducer';
import { DefineLeavesInformation } from 'src/models/leave';
import { createDefineLeaves, getAllLeaveTypeNames } from 'src/services/DefineLeaveService';
import { getAllGenders } from 'src/services/employeeService';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import { initiateLeaveTypes } from 'src/services/leaveService';
import { LeaveType } from './enum/DefineLeaves';

const AddDefineLeaves = ({ toggle, open, getData, length }) => {
  const dispatch = useDispatch();
  const TextFieldStyle = {
    paddingTop: 4,
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const LabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px'
  };

  const CheckboxLabelStyle = {
    paddingLeft: 15,
    paddingRight: 15,
    fontSize: '13px',
    color: '#ffffff'
  }

  const selectStyle = {
    width: '94%',
    marginLeft: 15,
    marginTop: 5,
    paddingRight: 15,
    fontSize: '13px'
  };

  const { register, handleSubmit, reset, setValue, getValues } = useForm<DefineLeavesInformation>();

  const [ employeeTypeError, setEmployeeTypeError ] = useState('');
  const [ leaveTypeError, setLeaveTypeError ] = useState('');
  const [ genderError, setGenderError ] = useState('');
  const [ leaveNoDaysError, setLeaveNoDaysError ] = useState('');
  const [ renewPeriodError, setRenewPeriodError ] = useState('');
  const [ hrApprovalError, setHrApprovalError ] = useState('');

  const [ employeeType, setEmployeeType ] = useState('');
  const [ leaveType, setLeaveType ] = useState('');
  const [ typeName, setTypeName ] = useState('');
  const [ gender, setGender ] = useState('');
  const [ leaveNoDays, setLeaveNoDays] = useState<string | number>('');
  const [ renewPeriod, setRenewPeriod ] = useState('');
  const [isHrApproval, setIsHrApproval] = useState(false);

  const [ employeeTypeList, setEmployeeTypeList ] = useState(['PERMANENT', 'PROBATION', 'INTERN']);
  const [ renewPeriodList, setRenewPeriodList ] = useState(['ANNUALLY', 'MONTHLY', 'N/A']);
  const [ genderList, setGenderList ] = useState([]);
  const [ leaveTypeNameList, setLeaveTypeNameList ] = useState([]); 

  const getAllGenderDetails = () => {
    getAllGenders().then((res) => {
      setGenderList(res?.data);
    });
  };

  useEffect(() => {
    getAllGenderDetails(); 
  }, []);

  const getAllLeaveTypeNameDetails = () => {
    getAllLeaveTypeNames().then((res) => {
      setLeaveTypeNameList(res.data.data);
    });
  };

  const initiateLeaveType = (leaveType: string, employmentType: string) => {
    dispatch(AppActions.showLoader());
    const data = {
      leaveType: leaveType,
      employmentType: employmentType
    }
    initiateLeaveTypes(data).then(() => {
      dispatch(AppActions.hideLoader());
    }).catch(() => {
      dispatch(AppActions.hideLoader());
    });
  }

  useEffect(() => {
    getAllLeaveTypeNameDetails();
  }, []);

  const [isConfirm, setIsConfirm]=useState(false);
  
  const handleConfirmToggle = () => {
    const data = getValues();
    if (isConfirm) {
      setIsConfirm(false);
    }
    if (
      data.employmentType !== '' &&
      data.gender !== '' &&
      data.renewPeriod !== '' &&
      data.typeName !== '' &&
      data?.hrApproved !== null 
    ) 
    {
      setEmployeeTypeError('');
      setLeaveTypeError('');
      setGenderError('');
      setRenewPeriodError('');
      setHrApprovalError('');

      if(
        (data.typeName === LeaveType.LIEU_LEAVE) || 
        (data.typeName === LeaveType.NO_PAY_LEAVE) || 
        (data.typeName === LeaveType.WORK_FROM_HOME) ||
        (data.typeName === LeaveType.CARRY_FORWARD_LEAVE)) {
        setLeaveNoDaysError('');
        setIsConfirm(!isConfirm);
      }else{
        if(data.noOfDays.trim().length > 0 && parseFloat(data.noOfDays) > 0.0) {
          setLeaveNoDaysError('');
          setIsConfirm(!isConfirm);
        }
      }

    } if (!employeeType) {
      setEmployeeTypeError('Employee Type is required.');
    } if (!data.typeName) {
        setLeaveTypeError('Leave Type is required.');
    } if (!gender) {
        setGenderError('Gender is required.');
    }
    if (!renewPeriod) {
      setRenewPeriodError('Renew Period is required.');
    }
    if(
      (data.typeName === LeaveType.LIEU_LEAVE) || 
      (data.typeName === LeaveType.NO_PAY_LEAVE) || 
      (data.typeName === LeaveType.WORK_FROM_HOME) || 
      (data.typeName === LeaveType.CARRY_FORWARD_LEAVE)
    ) {
      setLeaveNoDaysError('');
    }else{
     if (data.noOfDays === '') {
        setLeaveNoDaysError('Leave No Days is required.');
    } else if (data.noOfDays.trim().length < 1) {
        setLeaveNoDaysError('Leave No Days cannot be only spaces');
    } else if (Number(parseFloat(data.noOfDays)) <= 0.0) {
        setLeaveNoDaysError('Leave No Days cannot be only 0.');
    }}
  };

  const onClose = () => {
    reset();
    setValue('employmentType', '');
    setEmployeeType('');
    setLeaveType('');
    setGender('');
    setLeaveNoDays('');
    setRenewPeriod('');
    setEmployeeTypeError('');
    setLeaveTypeError('');
    setGenderError('');
    setLeaveNoDaysError('');
    setRenewPeriodError('');
    setHrApprovalError('');
    setIsHrApproval(false);
    toggle();
  };

  const useAppSelector: TypedUseSelectorHook<RootStateType> = useSelector;
  const { isLoading } = useAppSelector((state) => state.appReducer);

  const onSubmit = () => {
    const data = getValues();
    if (
      data.employmentType !== '' &&
      data.gender !== '' &&
      data.renewPeriod !== '' &&
      data.typeName !== '' &&
      data.hrApproved !== null 
    ) {
      dispatch(AppActions.showLoader());
        setEmployeeTypeError('');
        setLeaveTypeError('');
        setGenderError('');
        setRenewPeriodError('');
        setHrApprovalError('');

        if((data.typeName === LeaveType.LIEU_LEAVE) || (data.typeName === LeaveType.NO_PAY_LEAVE) || (data.typeName === LeaveType.WORK_FROM_HOME)) {
          setLeaveNoDaysError('');
        }else{
          if(data.noOfDays !== '' && data.noOfDays.trim().length > 0 && parseFloat(data.noOfDays) > 0.0) {
            setLeaveNoDaysError('');
          }
        }

      const body = {
        employmentType: data.employmentType,
        typeName: data.typeName,
        description: "null",
        gender: data.gender,
        noOfDays: parseFloat(data.noOfDays),
        renewPeriod: data?.renewPeriod === 'N/A' ? 'NA' : data.renewPeriod,
        hrApproved: data.hrApproved,
        status: null
      };

      createDefineLeaves(body)
        .then((res) => {
          if (res.data?.statusCode === 200) {
            //Temporarily added until BE will be deployed
            initiateLeaveType(body.typeName, body.employmentType);
          }
          reset();
          onClose()
          setEmployeeType('');
          setLeaveType('');
          setGender('');
          setLeaveNoDays('');
          setRenewPeriod('');
          setIsHrApproval(false);
          dispatch(
            AppActions.toggleSnackBarOpen({
              message: 'You’ve created the leave type successfully.',
              severity: 'success'
            })
          );
          getData();
          toggle();
          handleConfirmToggle();
          setLeaveTypeError('');
          setLeaveNoDaysError('');
          dispatch(AppActions.hideLoader());
        })
        .catch((error) => {
          dispatch(AppActions.hideLoader());
          dispatch(
            AppActions.toggleSnackBarOpen({
              message: error.response.data.message,
              severity: 'error'
            })
          );
          handleConfirmToggle();
          dispatch(AppActions.hideLoader());
        });
      } if (!employeeType) {
        setEmployeeTypeError('Employee Type is required.');
      } if (!data.typeName) {
          setLeaveTypeError('Leave Type is required.');
      } if (!gender) {
          setGenderError('Gender is required.');
      }
      if (!renewPeriod) {
          setRenewPeriodError('Renew Period is required.');
      }
      if((data.typeName === LeaveType.LIEU_LEAVE) || (data.typeName === LeaveType.NO_PAY_LEAVE) || (data.typeName === LeaveType.WORK_FROM_HOME)) {
        setLeaveNoDaysError('');
      }else{
       if (data.noOfDays === '') {
          setLeaveNoDaysError('Leave No Days is required.');
      } else if (data.noOfDays.trim().length < 1) {
          setLeaveNoDaysError('Leave No Days cannot be only spaces');
      } else if (parseFloat(data.noOfDays) < 0.0) {
          setLeaveNoDaysError('Leave No Days cannot be only 0.');
      }}
  };

  const validateHrApprove = () => {
    setIsHrApproval(!isHrApproval);
  }

  const leaveTypeHandler = (selectLeaveType: string) => {
    setLeaveType(selectLeaveType);

    if(selectLeaveType === LeaveType.LIEU_LEAVE) {
      setLeaveNoDays('');
      setLeaveNoDaysError('');
      setRenewPeriod('N/A');
      setValue('renewPeriod', 'N/A');
    }else if (selectLeaveType === LeaveType.SHORT_LEAVE) {
      setLeaveNoDays(2);
      setRenewPeriod('MONTHLY');
      setValue('noOfDays', '2');
      setValue('renewPeriod', 'MONTHLY');
    }else if ((selectLeaveType === LeaveType.NO_PAY_LEAVE) || (selectLeaveType === LeaveType.WORK_FROM_HOME)) {
      setLeaveNoDays('');
      setRenewPeriod('N/A');
      setValue('renewPeriod', 'N/A');
      setLeaveNoDaysError('');
      setRenewPeriodError('');
    }else if (selectLeaveType === LeaveType.CARRY_FORWARD_LEAVE) {
      setLeaveNoDays(0);
      setValue('noOfDays', '0');
      setLeaveNoDaysError('');
      setGender('ALL');
      setValue('gender', 'ALL');
    }else {
      setLeaveNoDays('');
      setRenewPeriod('');
    }
  }

  return (
    <Fragment>
      {' '}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid item xs={12} sx={{ pt: 3, pb: 1, pl: 2 }}>
          <Typography style={{ fontSize: '15px' }} variant="h3">
            Create Leave
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Employment Type<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              MenuProps={{ autoFocus: false }}
              labelId="search-select-label"
              style={{
                height: '2.25rem',
                width: '94%',
                marginLeft: 15,
                marginTop: 5,
                paddingRight: 15,
                fontSize: '13px'
              }}
              id="search-select"
              displayEmpty
              value={employeeType}
              defaultValue=""
              {...register('employmentType', {})}
              onChange={(e) => {
                setEmployeeType(e.target.value);
              }}
              onClose={() => setEmployeeTypeError('')}
            >
              <MenuItem style={{ display: 'none' }} selected disabled value={''}>
                Select Employment Type
              </MenuItem>
              {employeeTypeList.length > 0 &&
                employeeTypeList?.map((option, i) => (
                  <MenuItem key={i} value={option}>
                    {option}
                  </MenuItem>
                ))}
            </Select>
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {employeeTypeError}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
            Leave Type<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              className="select-text"
              size="small"
              style={selectStyle}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
              {...register('typeName', {})}
              value={leaveType}
              defaultValue={''}
              onChange={(e) => {
                leaveTypeHandler(e.target.value);
              }}
              onClose={() => setLeaveTypeError('')}
            >
              <MenuItem style={{ display: 'none' }} selected disabled value={''}>
                Select Leave Type
              </MenuItem>
              {leaveTypeNameList.length > 0 &&
                leaveTypeNameList?.map((option, i) => (
                  <MenuItem key={i} value={option.name}>
                    {option.name}
                  </MenuItem>
                ))}
            </Select>
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {leaveTypeError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Gender <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              disabled={leaveType === LeaveType.CARRY_FORWARD_LEAVE}
              className="select-text"
              size="small"
              style={selectStyle}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
              {...register('gender', {})}
              value={gender}
              defaultValue={''}
              onChange={(e) => {
                setGender(e.target.value.toUpperCase());
              }}
              onClose={() => setGenderError('')}
            >
              <MenuItem style={{ display: 'none' }} selected disabled value={''}>
                Select Gender
              </MenuItem>
              {genderList.length > 0 &&
                genderList?.map((option, i) => (
                  <MenuItem key={i} value={option.name.toUpperCase()}>
                    {option.name.toUpperCase()}
                  </MenuItem>
                ))}
            </Select>
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {genderError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Leave No of Days
              {((leaveType !== LeaveType.LIEU_LEAVE) && 
              (leaveType !== LeaveType.NO_PAY_LEAVE) && 
              (leaveType !== LeaveType.WORK_FROM_HOME) && 
              (leaveType !== LeaveType.CARRY_FORWARD_LEAVE)) 
              && (<span style={{ color: 'red' }}>*</span>)}
            </Typography>
            <input
              maxLength={30}
              disabled={
                (leaveType === LeaveType.LIEU_LEAVE) || 
                (leaveType === LeaveType.SHORT_LEAVE) || 
                (leaveType === LeaveType.NO_PAY_LEAVE) || 
                (leaveType === LeaveType.WORK_FROM_HOME) ||
                (leaveType === LeaveType.CARRY_FORWARD_LEAVE)
              }
              {...register('noOfDays', {})}
              value={(leaveType === LeaveType.CARRY_FORWARD_LEAVE) ? '---' : leaveNoDays}
              style={{
                marginTop: 4,
                marginLeft: 15,
                width: '27.7rem',
                fontSize: '13px',
                backgroundColor: 
                (leaveType === LeaveType.LIEU_LEAVE) || 
                (leaveType === LeaveType.SHORT_LEAVE) || 
                (leaveType === LeaveType.NO_PAY_LEAVE) || 
                (leaveType === LeaveType.WORK_FROM_HOME) ||
                (leaveType === LeaveType.CARRY_FORWARD_LEAVE)
                ? '#C0C0C0' : '',
                borderRadius: '4px 4px',
                height: '2.3rem',
                borderStyle: 'solid',
                borderWidth: '1px',
                paddingLeft: '0.8rem'
              }}
              placeholder={
                ((leaveType === LeaveType.LIEU_LEAVE) || 
                (leaveType === LeaveType.NO_PAY_LEAVE) || 
                (leaveType === LeaveType.WORK_FROM_HOME) ||
                (leaveType === LeaveType.CARRY_FORWARD_LEAVE)) 
                ? '---' : 'Enter No of Days'}
              onChange={(e) => {
                if (e.target.value.length > 50) {
                  setLeaveNoDaysError('Leave No Days must not exceed 50 characters');
                } else {
                  setLeaveNoDaysError('');
                  setLeaveNoDays(e.target.value);
                }
              }}
            />
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {leaveNoDaysError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={LabelStyle} fontWeight="light" variant="h5">
              Renew Period<span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select
              MenuProps={{ autoFocus: false }}
              labelId="search-select-label"
              {...register('renewPeriod', {})}
              style={{
                height: '2.25rem',
                width: '94%',
                marginLeft: 15,
                marginTop: 5,
                paddingRight: 15,
                fontSize: '13px'
              }}
              id="search-select"
              displayEmpty
              value={renewPeriod}
              defaultValue=""
              disabled={
                (leaveType === LeaveType.SHORT_LEAVE) || 
                (leaveType === LeaveType.NO_PAY_LEAVE) || 
                (leaveType === LeaveType.WORK_FROM_HOME) || 
                (leaveType === LeaveType.LIEU_LEAVE)
              }
              onChange={(e) => {
                setRenewPeriod(e.target.value);
              }}
              onClose={() => setRenewPeriodError('')}
            >
              <MenuItem style={{ display: 'none' }} selected disabled value={''}>
                Select Renew Period
              </MenuItem>
              {renewPeriodList.length > 0 &&
                renewPeriodList?.map((option, i) => (
                  <MenuItem key={i} value={option}>
                    {option}
                  </MenuItem>
                ))}
            </Select>
            <Typography
              style={{
                paddingLeft: 15,
                paddingRight: 15,
                fontSize: '11px'
              }}
              color="red"
            >
              {renewPeriodError}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography style={CheckboxLabelStyle} fontWeight="light" variant="h5">
              HR Approval
            </Typography>
            <FormControlLabel
              style={TextFieldStyle}
              control={<Checkbox {...register('hrApproved')} checked={isHrApproval}/>}
              id="create-account"
              onClick={validateHrApprove}
              label={
                <Typography style={{ fontSize: '12px' }}>
                  HR Approval
                </Typography>
              }
            />
          </Grid>
          <Grid
            item
            sx={{
              mb: 2,
              mt: 2,
              pr: 2,
              display: 'flex',
              justifyContent: 'flex-end'
            }}
            xs={12}
          >
            <Button
              sx={{
                width: '10%',
                fontWeight: 'normal',
                background: '#F5F7FF',
                fontSize: '13px'
              }}
              onClick={() => {
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={isLoading}
              className={isLoading ? 'loading' : ''}
              sx={{
                ml: 2,
                width: '10%',
                fontSize: '13px',
                fontWeight: 'normal',
                background: '#0069E4'
              }}
              variant="contained"
              type="button"
              onClick={handleConfirmToggle}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </form>
      <ConfirmationModal
      isLoading={isLoading}
      open={isConfirm}
      toggle={handleConfirmToggle}
      message={`Are you sure you want to create this leave type?`}
      onConfirm={() => onSubmit()}
      noToggle={handleConfirmToggle}
    />
  </Fragment>
  );
};

export default AddDefineLeaves;
